import React from 'react';
import { useContext, useEffect, useRef, useState } from "react";
import invariant from 'tiny-invariant';
import { Copy, Ellipsis, Plus } from 'lucide-react';

import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { unsafeOverflowAutoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/unsafe-overflow/element';

import { useTranslation } from "react-i18next";
import { useApp } from '../../context/AppContext.jsx';
import { useSave } from '../../context/SaveContext.jsx';
import { useAppNavigate } from '../../page/Route.jsx';
import { MyEditableText, MyModal, Hr } from '../ui/UI.jsx';

import {
	getColumnData,
	isCardData,
	isCardDropTargetData,
	isColumnData,
	isDraggingACard,
	isDraggingAColumn
} from './Data.jsx';


import { Card, CardShadow } from "./Card.jsx";
import { isShallowEqual } from '@MyLib/System/isShallowEqual.js';
import { SettingsContext } from "./SettingsContext.js";

const ColumnState = {
	IsCardOver: 'is-card-over',
	IsColumnOver: 'is-column-over',
	Idle: 'idle',
	IsDragging: 'is-dragging'
}
const idle = { type: ColumnState.Idle };

const stateStyles = {
	[ColumnState.Idle]: 'cursor-grab',//要修正
	[ColumnState.IsCardOver]: 'outline outline-2 outline-neutral-50',//要修正
	[ColumnState.IsDragging]: 'opacity-50',
	[ColumnState.IsColumnOver]: 'bg-dark'
};

const CardList = ({ column, cardSelector }) =>
{
	const {buildCardDatas} = useApp();

	const renderCard = (card) => {
		const data = buildCardDatas.find(c => c.uuid === card.uuid);
		return {
			name: data ? data.name : "---",
			setCode: data ? data.setCode : "",
			manaCost: data ? data.manaCost : "",
		};
	};

	return column.cards.map((card, index) =>
		<Card
			key={index}
			columnId={column.id}
			card={card}
			cardData={renderCard(card)}
			cardSelector={cardSelector}
		/>
	);
};

export const Column = ({ column, index, addCard, deleteColumn, copyColumn, pasteColumn, cardSelector }) =>
{
	const {t} = useTranslation();
	const {updateBuildColumnName} = useSave();
	const {keepCards} = useApp();
	const {gotoCardSearch} = useAppNavigate();

	const scrollableRef = useRef(null);
	const outerFullHeightRef = useRef(null);
	const headerRef = useRef(null);
	const innerRef = useRef(null);
	const { settings } = useContext(SettingsContext);
	const [state, setState] = useState(idle);

	const [menu, setMenu] = useState(false);
	const buttonRef = useRef();
	const [buttonRect, setButtonRect] = useState();

	useEffect(() => {
		const outer = outerFullHeightRef.current;
		const scrollable = scrollableRef.current;
		const header = headerRef.current;
		const inner = innerRef.current;
		invariant(outer);
		invariant(scrollable);
		invariant(header);
		invariant(inner);

		const data = getColumnData({ column });

		function setIsCardOver({ data, location }) {
			const innerMost = location.current.dropTargets[0];
			const isOverChildCard = Boolean(innerMost && isCardDropTargetData(innerMost.data));

			const proposed = {
				type: ColumnState.IsCardOver,
				dragging: data.rect,
				isOverChildCard,
			};
			// optimization - don't update state if we don't need to.
			setState((current) => {
				if (isShallowEqual(proposed, current)) {
					return current;
				}
				return proposed;
			});
		}

		return combine(
			draggable({
				element: header,
				getInitialData: () => data,
				onGenerateDragPreview({ source, location, nativeSetDragImage }) {
					const data = source.data;
					invariant(isColumnData(data));
					setCustomNativeDragPreview({
						nativeSetDragImage,
						getOffset: preserveOffsetOnSource({ element: header, input: location.current.input }),
						render({ container }) {
							// Simple drag preview generation: just cloning the current element.
							// Not using react for this.
							const rect = inner.getBoundingClientRect();
							const preview = inner.cloneNode(true);
							invariant(preview instanceof HTMLElement);
							preview.style.width = `${rect.width}px`;
							preview.style.height = `${rect.height}px`;

							// rotation of native drag previews does not work in safari
							//isSafari

							container.appendChild(preview);
						}
					})
				},
				onDragStart() {
					setState({ type: ColumnState.IsDragging });
				},
				onDrop() {
					setState(idle);
				}
			}),
			dropTargetForElements({// ドロップ先の要素を設定
				element: outer,
				getData: () => data,
				canDrop({ source }) {
					return isDraggingACard({ source }) || isDraggingAColumn({ source });
				},
				getIsSticky: () => true,
				onDragStart({ source, location }) {
					if (isCardData(source.data)) {
						setIsCardOver({ data: source.data, location });
					}
				},
				onDragEnter({ source, location }) {
					if (isCardData(source.data)) {
						setIsCardOver({ data: source.data, location });
						return;
					}
					if (isColumnData(source.data) && source.data.column.id !== column.id) {
						setState({ type: ColumnState.IsColumnOver });
					}
				},
				onDropTargetChange({ source, location }) {
					if (isCardData(source.data)) {
						setIsCardOver({ data: source.data, location });
						return;
					}
				},
				onDragLeave({ source }) {
					if (isColumnData(source.data) && source.data.column.id === column.id) {
						return;
					}
					setState(idle);
				},
				onDrop() {// ドロップ
					setState(idle);
				}
			}),
			autoScrollForElements({
				canScroll({ source }) {
					if (!settings.isOverElementAutoScrollEnabled) {
						return false;
					}
					return isDraggingACard({ source });
				},
				getConfiguration: () => ({ maxScrollSpeed: settings.columnScrollSpeed }),
				element: scrollable
			}),
			unsafeOverflowAutoScrollForElements({
				element: scrollable,
				getConfiguration: () => ({ maxScrollSpeed: settings.columnScrollSpeed }),
				canScroll({ source }) {
					if (!settings.isOverElementAutoScrollEnabled) {
						return false;
					}
					if (!settings.isOverflowScrollingEnabled) {
						return false;
					}
					return isDraggingACard({ source });
				},
				getOverflow() {
					return {
						forTopEdge: {
							top: 1000,
						},
						forBottomEdge: {
							bottom: 1000,
						},
					};
				}
			})
		);
	}, [column.id, settings]);

	return (
		<div
			ref={outerFullHeightRef}
			className="h-100 d-flex flex-column p-0"
		>
			<div
				ref={innerRef}
				className={`h-100 d-flex flex-column rounded-3 ${stateStyles[state.type]}`}
			>
				<div
					className={`d-flex flex-column rounded-3 gap-1 p-1 my-bg-column ${state.type === ColumnState.IsColumnOver ? 'invisible' : ''}`}
					style={{ width: '320px', maxHeight: "100%", userSelect: 'none' }}
				>
					{/* Header */}
					<div
						className="d-flex justify-content-between align-items-center"
						ref={headerRef}
					>
						<MyEditableText
							value={column.name}
							displayClass="fw-bold"
							inputClass="form-control form-control-sm"
							onChange={(name)=>{updateBuildColumnName(index, name)}}
						/>
						<button
							className="btn btn-secondary btn-sm rounded-circle"
							ref={buttonRef}
							onClick={()=>{
								setMenu(true);
								const rect = buttonRef.current.getBoundingClientRect();
								setButtonRect(rect);
							}}
						>
							<Ellipsis size={16} />
						</button>
					</div>
					{/* CardList */}
					<div
						className="d-flex flex-column flex-grow-1 overflow-auto gap-1"
						ref={scrollableRef}
					>
						<CardList
							column={column}
							cardSelector={cardSelector}
						/>
						{ state.type === ColumnState.IsCardOver && !state.isOverChildCard ? (
							<div className="flex-shrink-0">
								<CardShadow dragging={state.dragging} />
							</div>
							) : null
						}
					</div>
					{/* Footer */}
					{((keepCards.length >= 0) && 
						<div className="d-flex">
							<button
								className="btn btn-secondary d-flex flex-grow-1 align-items-center"
								onClick={() => {
									if (keepCards.length > 0) {
										addCard(index);
									}
									else {
										gotoCardSearch();
									}
								}}
							>
								<Plus />
								<div>{t("AddCard")}</div>
							</button>
						</div>
					)}
				</div>
			</div>
			{menu && (
				<>
					{/* backdrop */}
					<MyModal
						onClick={() => setMenu(false)}
					/>
						{/* メニュー */}
						<div
							className="position-fixed"
							style={{
								top: buttonRect.top + buttonRect.height,
								left: buttonRect.left,
								zIndex: 1550,
							}}
						>
						<div className="bg-white border rounded shadow-sm m-1">
							<button className="dropdown-item p-1" onClick={async ()=>{
								await copyColumn(index);
								setMenu(false);
							}} >{t("Common_Copy")}</button>
							<button className="dropdown-item p-1" onClick={async ()=>{
								await pasteColumn(index);
								setMenu(false);
							}} >{t("Common_Paste")}</button>
							<Hr />
							<button className="dropdown-item p-1" onClick={()=>{
								deleteColumn(index);
								setMenu(false);
							}} >{t("DeleteGroup")}</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
};
